import Spacer from "@/components/Spacer";
import Text from "@/components/Text";
import AuthWrapper from "@/layout/AuthWrapper";
import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import Button from "@/components/Button";
import { useSignin } from "./useSignIn";
import { useReality } from "@/context/RealityContext";
import Dialog from "@/components/Dialog";
import PhoneInput from "@/components/PhoneInput";
import ResendCodeButton from "@/components/ResendCodeButton";
import Theme from "@/utils/theme";
import InfoIcon from "@/ui/icons/InfoIcon";
import { OtpCodeInput } from "../SignUp/styles";
import { ENABLE_POINTS_UI } from "@/utils/feature-flags";
import { realityInfo } from "@/utils/realityInfo";

export default function SignIn() {
  const {
    currentStep,
    setCurrentStep,

    phoneNumber,
    setPhoneNumber,
    phoneNumberError,

    otp,
    setOtp,

    onClickSignIn,
    isLoading,
    goToSignUp,
    isOpenChooseValidation,
    setIsOpenChooseValidation,

    showSubscriptionsEndedDialog,
    toggleSubscriptionsEndedDialog,

    smsTimerValue,
    sendSignInSmsCode,
    onFixPhoneNumber,

    showNoAccountDialog,
    setShowNoAccountDialog,
    isOpenBlacklistedModal,
    setIsOpenBlacklistedModal,
    resendCode,

    isSendingCode,
  } = useSignin();
  const { isSubscriptionOpen } = useReality();
  const [showSubscriptionsUnavailableDialog, setShowSubscriptionsUnavailableDialog] =
    useState(false);
  const hasOpenedSubscriptionsUnavailableDialog = useRef(false);

  useEffect(() => {
    if (!isSubscriptionOpen && !hasOpenedSubscriptionsUnavailableDialog.current) {
      setShowSubscriptionsUnavailableDialog(true);
      hasOpenedSubscriptionsUnavailableDialog.current = true;
    }
  }, [isSubscriptionOpen]);

  const goBackToPhoneStep = () => {
    setCurrentStep(1);
    setOtp("");
  };

  const goToWebsite = () => {
    window.location.href = realityInfo.websiteUrl!;
  };

  return (
    <AuthWrapper
      pageTitle="FAÇA O LOGIN"
      stepsSubtitle="Cadastre-se e conclua os desafios!"
      stepsDescription={
        ENABLE_POINTS_UI
          ? "Após concluir seu cadastro, faça o login e acesse a página do participante para pontuar concluindo cursos e desafios e avançar na competição."
          : "Após concluir seu cadastro, faça o login e acesse a página do participante para completar cursos e desafios e avançar na competição."
      }
      onClickBack={currentStep > 1 ? goBackToPhoneStep : undefined}
    >
      <div id="recaptcha-container" />

      {currentStep === 1 && (
        <Box display="flex" flexDirection="column">
          <Text fontSize="1rem" mt="0.5rem" mb="1rem" alignSelf="center">
            Entre aqui
          </Text>

          <PhoneInput
            label="Telefone"
            value={phoneNumber}
            setValue={setPhoneNumber}
            error={!!phoneNumberError}
            errorText={phoneNumberError}
          />

          <Spacer height="2rem" />
          <Button size="large" onClick={onClickSignIn}>
            {isLoading ? (
              <CircularProgress style={{ color: "white" }} size="1rem" />
            ) : (
              "Entrar"
            )}
          </Button>

          <Spacer height="1rem" />
          <Button
            size="large"
            color="secondary"
            onClick={() => goToSignUp()}
            disabled={!isSubscriptionOpen}
          >
            Quero me cadastrar
          </Button>
        </Box>
      )}

      {currentStep === 2 && (
        <Box display="flex" flexDirection="column" alignItems="center">
          <Text
            fontSize="0.9rem"
            color={Theme.colors.text.secondary}
            mb={1.5}
            textAlign="center"
          >
            Digite o código de verificação
          </Text>

          <OtpCodeInput
            value={otp}
            onChange={setOtp}
            onSubmit={onClickSignIn}
            isInvalid={false}
          />

          <Spacer height="1.5rem" />
          <Text color={Theme.colors.text.secondary} fontSize="0.875rem">
            Tempo de reenvio do código: {smsTimerValue}
          </Text>

          <Spacer height="1.25rem" />
          <ResendCodeButton
            disabled={smsTimerValue > 0 || isSendingCode}
            onClick={resendCode}
          />

          <Button
            variant="text"
            fullWidth={false}
            onClick={goBackToPhoneStep}
            style={{ marginTop: "1rem" }}
          >
            <Text fontSize="0.9rem" color={Theme.colors.brand.secondary}>
              Editar telefone
            </Text>
          </Button>

          <Spacer height="1.5rem" />
          <Button size="large" fullWidth onClick={onClickSignIn}>
            {isLoading ? (
              <CircularProgress style={{ color: "white" }} size="1rem" />
            ) : (
              "Validar código"
            )}
          </Button>
        </Box>
      )}

      <Dialog
        isOpen={showNoAccountDialog}
        text="Este telefone não possui cadastro, crie sua conta para prosseguir."
        content={
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            mb="1.5rem"
            px="1rem"
          >
            <Text fontSize="1.1rem" fontWeight="700" display="block" textAlign="center">
              {phoneNumber || "—"}
            </Text>
          </Box>
        }
        confirmButtonText="Criar Conta"
        confirmButtonDisabled={!isSubscriptionOpen}
        onConfirm={() => goToSignUp({ fromLoginNoAccount: true })}
        onClose={() => setShowNoAccountDialog(false)}
      />

      <Dialog
        isOpen={isOpenBlacklistedModal}
        text={realityInfo.blacklistModalText}
        confirmButtonText="Sair"
        onConfirm={goToWebsite}
        onClose={() => setIsOpenBlacklistedModal(false)}
      />

      <Dialog
        isOpen={showSubscriptionsEndedDialog}
        text="Período de inscrições foi encerrado."
        onConfirm={toggleSubscriptionsEndedDialog}
      />

      <Dialog
        isOpen={showSubscriptionsUnavailableDialog}
        text={
          "As inscrições desta edição do Pedreiro Top Brasil foram encerradas. Para participar, aguarde a próxima edição.\n\nJá tem cadastro? Entre com seu telefone para acompanhar sua participação na plataforma."
        }
        onConfirm={() => setShowSubscriptionsUnavailableDialog(false)}
      />

      <Dialog
        isOpen={isOpenChooseValidation}
        onDismiss={() => setIsOpenChooseValidation(false)}
        isLoading={isSendingCode}
        preventBackdropClose={isSendingCode}
        maxWidth="22rem"
        icon={
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
            px="1rem"
          >
            <InfoIcon size={22} />
            <Text
              fontWeight={700}
              fontSize="0.95rem"
              textAlign="center"
              mt={1.25}
              lineHeight={1.25}
            >
              Número de celular já possui cadastrado
            </Text>
            <Text
              fontSize="0.875rem"
              color={Theme.colors.text.secondary}
              textAlign="center"
              mt={1}
              lineHeight={1.35}
            >
              Faça a validação por código pelo número de celular abaixo para entrar na plataforma
            </Text>
          </Box>
        }
        content={
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            px="1rem"
            mt={1.25}
            mb="1.75rem"
          >
            <Text fontSize="1.1rem" fontWeight="700" textAlign="center">
              {phoneNumber || "—"}
            </Text>
          </Box>
        }
        confirmButtonText="Receber código por SMS"
        onConfirm={() => sendSignInSmsCode()}
        closeButtonText="Corrigir número"
        onClose={onFixPhoneNumber}
      />
    </AuthWrapper>
  );
}
